import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import { api, ApiError } from './api.ts';
import { SessionContext, type Me } from './session.ts';
import { FiltersProvider } from './filters.tsx';
import Layout from './components/Layout.tsx';
import Login from './pages/Login.tsx';
import { Spinner } from './components/common.tsx';

// Pages load on demand so the first screen doesn't wait for the charting library.
const Day = lazy(() => import('./pages/Day.tsx'));
const Trades = lazy(() => import('./pages/Trades.tsx'));
const TradeDetail = lazy(() => import('./pages/TradeDetail.tsx'));
const Calendar = lazy(() => import('./pages/Calendar.tsx'));
const Journal = lazy(() => import('./pages/Journal.tsx'));
const Performance = lazy(() => import('./pages/Performance.tsx'));
const Research = lazy(() => import('./pages/Research.tsx'));
const Start = lazy(() => import('./pages/Start.tsx'));
const PlaybookList = lazy(() => import('./pages/Playbook.tsx').then((m) => ({ default: m.PlaybookList })));
const PlaybookPage = lazy(() => import('./pages/Playbook.tsx').then((m) => ({ default: m.PlaybookPage })));
const Import = lazy(() => import('./pages/Import.tsx'));
const Settings = lazy(() => import('./pages/Settings.tsx'));
const More = lazy(() => import('./pages/More.tsx'));
const Admin = lazy(() => import('./pages/Admin.tsx'));
const Market = lazy(() => import('./pages/Market.tsx'));
const Practice = lazy(() => import('./pages/Practice.tsx'));

function Unreachable() {
  return (
    <div className="auth-wrap" role="status">
      <div className="auth-card card">
        <b>Can't reach Edgebook</b>
        <span className="muted small">Trying again automatically…</span>
      </div>
    </div>
  );
}

export default function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [unreachable, setUnreachable] = useState(false);

  // A server that's down or restarting says nothing about the session, so only a real answer signs out.
  const refresh = useCallback(async () => {
    try {
      setMe(await api.get<Me>('/api/auth/me'));
      setUnreachable(false);
    } catch (err) {
      if (err instanceof ApiError && err.status < 500) {
        setMe(null);
        setUnreachable(false);
      } else setUnreachable(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const onUnauthorized = () => setMe(null);
    window.addEventListener('tj:unauthorized', onUnauthorized);
    return () => window.removeEventListener('tj:unauthorized', onUnauthorized);
  }, [refresh]);

  useEffect(() => {
    if (!unreachable) return;
    const timer = setInterval(() => void refresh(), 3000);
    return () => clearInterval(timer);
  }, [unreachable, refresh]);

  if (me === undefined) return unreachable ? <Unreachable /> : null;
  if (me === null) return <Login onSignedIn={refresh} />;

  return (
    <SessionContext.Provider value={{ me, refresh, signOut: async () => { await api.post('/api/auth/logout'); setMe(null); } }}>
      <FiltersProvider>
        <Layout>
          <Suspense fallback={<Spinner />}>
            <Routes>
              <Route path="/" element={<Day />} />
              <Route path="/day/:date" element={<Day />} />
              <Route path="/trades" element={<Trades />} />
              <Route path="/trades/:id" element={<TradeDetail />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/start" element={<Start />} />
              <Route path="/research" element={<Research />} />
              <Route path="/research/:symbol" element={<Research />} />
              <Route path="/performance" element={<Performance />} />
              <Route path="/performance/:tab" element={<Performance />} />
              <Route path="/journal" element={<Journal />} />
              <Route path="/market/:tab?" element={<Market />} />
              <Route path="/practice" element={<Practice />} />
              {/* Old pages: a day's plan is its day page; Insights and Reports are Performance tabs. */}
              <Route path="/plan" element={<OldDayLink fallback="/" />} />
              <Route path="/insights" element={<Navigate to="/performance/discipline" replace />} />
              <Route path="/reports" element={<Navigate to="/performance/breakdown" replace />} />
              <Route path="/playbook" element={<PlaybookList />} />
              <Route path="/playbook/:setup" element={<PlaybookPage />} />
              <Route path="/import" element={<Import />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/more" element={<More />} />
              {me.isAdmin && <Route path="/admin" element={<Admin />} />}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Layout>
      </FiltersProvider>
    </SessionContext.Provider>
  );
}

/** /plan?date=… from before the Today page: open that day. */
function OldDayLink({ fallback }: { fallback: string }) {
  const [params] = useSearchParams();
  const date = params.get('date');
  return <Navigate to={date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? `/day/${date}` : fallback} replace />;
}
